<template>
  <b-modal
    id="booking-status-modal"
    ref="bookingStatusModal"
    :title="modalTitle"
    size="lg"
    @hidden="resetModal"
  >
    <b-form @submit.prevent="saveBookingStatus">
      <b-row>
        <b-col md="6">
          <b-form-group label="Invoice ID">
            <b-form-input v-model="bookingData.invoiceId" readonly />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Invoice Date">
            <b-form-input v-model="bookingData.invoiceDate" readonly />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Booking Status *" label-for="booking-status">
            <b-form-select
              id="booking-status"
              v-model="bookingData.bookingStatus"
              :options="bookingStatusOptions"
              required
            />
            <small v-if="error.bookingStatus" class="text-danger">{{ error.bookingStatus }}</small>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Booking Date *" label-for="booking-date">
            <flat-pickr
              class="form-control"
              id="booking-date"
              v-model="bookingData.bookingDate"
              :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
              required
            />
            <small v-if="error.bookingDate" class="text-danger">{{ error.bookingDate }}</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Booking Reason *" label-for="booking-reason">
            <b-form-select
              id="booking-reason"
              v-model="bookingData.bookingReason"
              :options="bookingReasonOptions"
              required
            />
            <small v-if="error.bookingReason" class="text-danger">{{ error.bookingReason }}</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row v-if="bookingData.bookingStatus === 'Not Booked'">
        <b-col md="12">
          <b-form-group label="Cancellation Reason" label-for="cancellation-reason">
            <b-form-select
              id="cancellation-reason"
              v-model="bookingData.cancellationReason"
              :options="cancellationReasonOptions"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <b-form-group label="Remarks">
            <b-form-textarea
              v-model="bookingData.remarks"
              rows="3"
              placeholder="Enter any additional remarks"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-alert v-if="submitError" variant="danger" show>
        {{ submitError }}
      </b-alert>
    </b-form>

    <div slot="modal-footer">
      <b-button
        variant="primary"
        @click="saveBookingStatus"
        :disabled="loading || !isFormValid"
      >
        <b-spinner v-if="loading" small class="mr-1" />
        Save Booking Status
      </b-button>
      <b-button variant="secondary" @click="$refs.bookingStatusModal.hide()">
        Cancel
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  VBModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BButton,
  BRow,
  BCol,
  BSpinner,
  BAlert
} from 'bootstrap-vue'
import moment from 'moment'
import flatPickr from 'vue-flatpickr-component'
import enhancedInvoiceService from '@/services/enhanced-invoice-service'
import invoiceConfigService from '@/services/invoice-config-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'BookingStatusModal',
  components: {
    BModal,
    VBModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BButton,
    BRow,
    BCol,
    BSpinner,
    BAlert,
    flatPickr,
    ToastificationContent
  },
  directives: {
    'b-modal': VBModal
  },
  props: {
    invoice: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      submitError: '',
      bookingData: {
        invoiceId: '',
        invoiceDate: '',
        bookingStatus: null,
        bookingDate: null,
        bookingReason: null,
        cancellationReason: null,
        remarks: ''
      },
      bookingStatusOptions: [
        { text: 'Select Status', value: null },
        { text: 'Booked', value: 'Booked' },
        { text: 'Not Booked', value: 'Not Booked' },
        { text: 'Pending', value: 'Pending' }
      ],
      bookingReasonOptions: [],
      cancellationReasonOptions: [],
      defaultBookingReasons: [
        'Revenue confirmed',
        'Client confirmation received',
        'PO / WO received',
        'Candidate joined',
        'Service delivered',
        'Monthly recurring billing',
        'Adjustment / correction',
        'Other',
      ],
      defaultCancellationReasons: [
        'Early Attrition',
        'Invoice Filed and cannot be Amended',
        'Bad Debt',
        'Client cancelled',
        'Duplicate invoice',
        'Other',
      ],
      error: {
        bookingStatus: '',
        bookingDate: '',
        bookingReason: ''
      }
    }
  },
  computed: {
    modalTitle() {
      if (!this.invoice) return 'Booking Status'
      const ref = this.invoice.invoice_id || this.invoice.invoice_number || this.invoice.id || ''
      return `Update Booking Status - ${ref}`
    },
    isFormValid() {
      return !!(
        this.bookingData.bookingStatus &&
        this.bookingData.bookingDate &&
        this.bookingData.bookingReason
      )
    }
  },
  async created() {
    await this.loadConfigurations()
  },
  methods: {
    mapReasonOption(reason) {
      if (reason == null || reason === '') return null
      if (typeof reason === 'string' || typeof reason === 'number') {
        return { text: String(reason), value: reason }
      }
      if (typeof reason !== 'object') return null
      const text = reason.label || reason.text || reason.name || reason.reason || reason.bookingReason
        || reason.cancellationReason || reason.value
      if (text == null || text === '') return null
      const value = reason.value != null && reason.value !== ''
        ? reason.value
        : (reason.id != null && reason.id !== '' ? reason.id : text)
      return { text: String(text), value }
    },
    buildReasonSelectOptions(raw, fallback = []) {
      const mapped = (Array.isArray(raw) ? raw : [])
        .map(item => this.mapReasonOption(item))
        .filter(Boolean)
      const extras = mapped.length
        ? mapped
        : (Array.isArray(fallback) ? fallback : []).map(item => this.mapReasonOption(item)).filter(Boolean)
      return [{ text: 'Select Reason', value: null }, ...extras]
    },
    async loadConfigurations() {
      try {
        const [bookingReasonsRes, cancellationReasonsRes] = await Promise.all([
          invoiceConfigService.getBookingReasons(),
          invoiceConfigService.getCancellationReasons()
        ])

        const bookingOk = bookingReasonsRes && bookingReasonsRes.status === 'OK'
        this.bookingReasonOptions = this.buildReasonSelectOptions(
          bookingOk ? bookingReasonsRes.data : [],
          this.defaultBookingReasons
        )

        const cancelOk = cancellationReasonsRes && cancellationReasonsRes.status === 'OK'
        this.cancellationReasonOptions = this.buildReasonSelectOptions(
          cancelOk ? cancellationReasonsRes.data : [],
          this.defaultCancellationReasons
        )
      } catch (error) {
        console.error('Error loading configurations:', error)
        this.bookingReasonOptions = this.buildReasonSelectOptions([], this.defaultBookingReasons)
        this.cancellationReasonOptions = this.buildReasonSelectOptions([], this.defaultCancellationReasons)
      }
    },
    /** Business invoice_id for GET/PUT …/booking/status (e.g. "TEST 2"), not numeric PK. */
    resolveBookingRecordId() {
      const source = this.invoice
      if (source == null || source === '') return null
      if (typeof source === 'object') {
        const key = source.invoice_id
          || source.invoice_number
          || source.invoiceId
          || source.id
          || null
        return key != null && key !== '' ? String(key) : null
      }
      return String(source)
    },
    formatInvoiceDateForInput(val) {
      if (val == null || val === '') return ''
      try {
        const m = moment(val)
        return m.isValid() ? m.format('YYYY-MM-DD') : String(val).slice(0, 10)
      } catch (e) {
        return ''
      }
    },
    hydrateInvoiceFieldsFromProp() {
      if (!this.invoice) return
      const idDisplay = this.invoice.invoice_id || this.invoice.invoice_number || (this.invoice.id != null ? String(this.invoice.id) : '')
      const rawDate = this.invoice.invoice_date || this.invoice.invoiceDate
      this.bookingData.invoiceId = idDisplay
      this.bookingData.invoiceDate = this.formatInvoiceDateForInput(rawDate)
    },
    async loadBookingStatus() {
      if (!this.invoice) return

      this.hydrateInvoiceFieldsFromProp()
      const recordId = this.resolveBookingRecordId()
      if (recordId === null || recordId === undefined || recordId === '') {
        return
      }

      this.loading = true
      try {
        const res = await enhancedInvoiceService.checkBookingStatus(recordId)
        const httpStatus = res && typeof res.status === 'number' ? res.status : null
        const body = (res && res.data && httpStatus != null) ? res.data : res
        const notFound = httpStatus === 404
          || (body && body.status === 'ERROR' && /not found/i.test(String(body.message || '')))
        if (body && body.status === 'OK' && body.data) {
          this.bookingData = {
            invoiceId: this.bookingData.invoiceId || this.invoice.invoice_id || this.invoice.invoice_number || '',
            invoiceDate: this.bookingData.invoiceDate || this.formatInvoiceDateForInput(this.invoice.invoice_date || this.invoice.invoiceDate),
            bookingStatus: body.data.bookingStatus || body.data.booking_status || null,
            bookingDate: body.data.bookingDate || body.data.booking_date || null,
            bookingReason: body.data.bookingReason || body.data.booking_reason || null,
            cancellationReason: body.data.cancellationReason || body.data.cancellation_reason || null,
            remarks: body.data.remarks || ''
          }
        } else {
          const invId = this.bookingData.invoiceId
          const invDt = this.bookingData.invoiceDate
          this.bookingData = {
            invoiceId: invId,
            invoiceDate: invDt,
            bookingStatus: this.invoice.booking_status || this.invoice.bookingStatus || null,
            bookingDate: this.invoice.booking_date || this.invoice.bookingDate || null,
            bookingReason: this.invoice.booking_reason || this.invoice.bookingReason || null,
            cancellationReason: this.invoice.cancellation_reason || this.invoice.cancellationReason || null,
            remarks: this.invoice.remarks || ''
          }
          if (notFound) {
            this.submitError = ''
          }
        }
      } catch (error) {
        console.error('Error loading booking status:', error)
        this.submitError = ''
      } finally {
        this.loading = false
      }
    },
    async saveBookingStatus() {
      if (!this.isFormValid) {
        this.validateForm()
        return
      }

      this.loading = true
      this.submitError = ''

      const data = {
        bookingStatus: this.bookingData.bookingStatus,
        bookingDate: moment(this.bookingData.bookingDate).format('YYYY-MM-DD'),
        bookingReason: this.bookingData.bookingReason,
        cancellationReason: this.bookingData.cancellationReason || null,
        remarks: this.bookingData.remarks || ''
      }

      try {
        const recordId = this.resolveBookingRecordId()
        if (!recordId) {
          this.submitError = 'Missing invoice identifier. Cannot update booking status.'
          return
        }
        const res = await enhancedInvoiceService.updateBookingStatus(recordId, data)
        if (res && res.status === 'OK') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: 'Booking status updated successfully!',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
          this.$emit('booking-status-updated', res.data)
          this.$refs.bookingStatusModal.hide()
        } else {
          this.submitError = res.message || 'Error updating booking status'
        }
      } catch (error) {
        console.error('Error saving booking status:', error)
        this.submitError = (error && error.message) || 'Error saving booking status. Please try again.'
      } finally {
        this.loading = false
      }
    },
    validateForm() {
      this.error = {
        bookingStatus: '',
        bookingDate: '',
        bookingReason: ''
      }

      if (!this.bookingData.bookingStatus) {
        this.error.bookingStatus = 'Booking status is required'
      }
      if (!this.bookingData.bookingDate) {
        this.error.bookingDate = 'Booking date is required'
      }
      if (!this.bookingData.bookingReason) {
        this.error.bookingReason = 'Booking reason is required'
      }
    },
    resetModal() {
      this.bookingData = {
        invoiceId: '',
        invoiceDate: '',
        bookingStatus: null,
        bookingDate: null,
        bookingReason: null,
        cancellationReason: null,
        remarks: ''
      }
      this.error = {
        bookingStatus: '',
        bookingDate: '',
        bookingReason: ''
      }
      this.submitError = ''
      this.loading = false
    },
    show() {
      this.$nextTick(() => {
        if (!this.bookingReasonOptions || this.bookingReasonOptions.length <= 1) {
          this.loadConfigurations()
        }
        if (this.invoice) {
          this.hydrateInvoiceFieldsFromProp()
          this.loadBookingStatus()
        }
        this.$refs.bookingStatusModal.show()
      })
    },
    hide() {
      this.$refs.bookingStatusModal.hide()
    }
  }
}
</script>
